import { api } from '@/lib/api.js';

// THE PAPERWORK DOORS (paperwork-1 D6, L2-1; routes/paperwork.routes.js). The checklist is derived on the server
// (utils/paperwork-checklist.js, one derivation); every surface reads it from here and derives nothing of its own: the
// Paperwork tab on the deal page and the cockpit's Distribute summary.
//
// getDealPaperwork answers { policy, checklist: { items, counts, not_in_standard_set }, documents, unread }. A deal with no
// policy row answers 409 NO_POLICY_ROW (expected: the caller says so in words, no generic toast).

const NO_POLICY = [{ status: 409, code: 'NO_POLICY_ROW' }];
// The undo and confirm refusals the tab renders on the row itself.
const DOOR_REFUSALS = [
  { status: 409, code: 'DOC_CHANGED' }, { status: 409, code: 'REPLACEMENT_LINKED' }, { status: 409, code: 'NOT_SUPERSEDED' },
  { status: 404, code: 'NOT_ON_THIS_POLICY' }, { status: 409, code: 'NOT_PLACED' }, { status: 409, code: 'NOT_NAMED' }, { status: 409, code: 'SUPERSEDED' },
];

export function getDealPaperwork(dealId) {
  return api.get(`/api/tracon/deals/${encodeURIComponent(dealId)}/paperwork`, { expected: NO_POLICY });
}

// "Undo: <old> is no longer replaced by <new>": the replacement the button showed is sent, so a page that went stale is refused.
export function undoDocumentReplacement(dealId, documentId, replacedBy) {
  return api.post(`/api/tracon/deals/${encodeURIComponent(dealId)}/paperwork/undo-replacement`, { document_id: documentId, replaced_by: replacedBy }, { expected: DOOR_REFUSALS });
}

// "Confirm it is this policy's" (A8): an imported or unconfirmed document becomes this policy's paper.
export function confirmPaperworkDocument(dealId, documentId) {
  return api.post(`/api/tracon/deals/${encodeURIComponent(dealId)}/paperwork/confirm`, { document_id: documentId }, { expected: DOOR_REFUSALS });
}

// THE PAPERWORK HOLDS (paperwork-1 D9, D11, L2-2; services/payment-holds.service.js on the server, the one function). The
// refusals a payment request create or "Signed paperwork received" answers in hold mode: expected, so no generic toast; the
// surface renders the hold with its button (PaymentRequestSheet, TransactionHistoryPanel).
export const HOLD_REFUSALS = Object.freeze(['SIGNED_APPLICATION_REQUIRED', 'SIGNED_APPLICATION_NOT_FILED', 'ENDORSEMENT_DOCUMENT_FIRST', 'ENDORSEMENT_UNMATCHED']
  .map((code) => Object.freeze({ status: 409, code })));
export const isHoldRefusal = (err) => !!err && err.status === 409 && HOLD_REFUSALS.some((x) => x.code === (err.body && err.body.code));

// The holds as the screens read them: { mode, signed_application (the window), waiver, endorsements, payment_request, can_waive,
// can_waive_why }. Nothing is recorded or refused by this read.
export function getPaymentHolds(dealId) {
  return api.get(`/api/tracon/deals/${encodeURIComponent(dealId)}/payment-holds`);
}

// "Send payment request without a signed application": owner or producer, own Google sign-in, a reason. The door's refusals
// are rendered beside the reason field.
const WAIVER_REFUSALS = [
  { status: 400, code: 'REASON_REQUIRED' }, { status: 400, code: 'REASON_TOO_LONG' }, { status: 409, code: 'WAIVER_EXISTS' },
  { status: 403, code: 'OWNER_OR_PRODUCER_ONLY' }, { status: 403, code: 'personal_privilege_only' }, { status: 403, code: 'PERSONAL_SIGN_IN_ONLY' },
];
export function recordPaymentRequestWaiver(dealId, reason) {
  return api.post(`/api/tracon/deals/${encodeURIComponent(dealId)}/payment-request-waiver`, { reason }, { expected: WAIVER_REFUSALS });
}

// "Revoke the waiver" (round 2, David Q5: "owner can revoke it mid-term (logged, reason)"): the owner, own Google sign-in, a
// reason. The answer is the revoked row read back; the refusals are rendered beside the reason field.
const REVOKE_REFUSALS = [
  { status: 400, code: 'REASON_REQUIRED' }, { status: 400, code: 'REASON_TOO_LONG' }, { status: 400, code: 'BAD_REQUEST' }, { status: 409, code: 'WAIVER_NOT_LIVE' },
  { status: 403, code: 'OWNER_ONLY' }, { status: 403, code: 'personal_privilege_only' }, { status: 403, code: 'PERSONAL_SIGN_IN_ONLY' },
];
export function revokePaymentRequestWaiver(dealId, waiverId, reason) {
  return api.post(`/api/tracon/deals/${encodeURIComponent(dealId)}/payment-request-waiver/revoke`, { waiver_id: waiverId, reason }, { expected: REVOKE_REFUSALS });
}
